import QuizInteractiveFlow from "@/components/storyboard/quiz/QuizInteractiveFlow";
import QuizCodeAssertion from "@/components/storyboard/quiz/QuizCodeAssertion";
import { deriveDefaultLanguage } from "@/components/storyboard/code/codeUtils";
import { useCappedCodeQuiz } from "@/hooks/useCappedCodeQuiz";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { QuizCodeAssertionEntryData } from "@/types/storyboard";

export interface QuizCodeAssertionFlowProps {
  id: string;
  title: string;
  entries: QuizCodeAssertionEntryData[];
  onValidate: (code: string, entryIndex: number) => Promise<ValidationResult>;
}

/**
 * Code assertion quiz embedded in the shared interactive-element flow. The
 * editor is seeded from the learner's saved submissions and stays editable
 * while the server grants attempts; "Code prüfen" runs server-side (LLM)
 * validation, which also persists the code. No localStorage — durable state
 * lives in validation_data, and so does the verdict the card recaps.
 *
 * Each task grants three tries, tracked per task: the learner can move between
 * tasks freely, and once none of them can be worked on the card offers
 * Ergebnis anzeigen.
 */
const QuizCodeAssertionFlow = ({
  id,
  title,
  entries,
  onValidate,
}: QuizCodeAssertionFlowProps) => {
  const language = deriveDefaultLanguage(
    entries.flatMap((entry) => [entry.codeStub, entry.testCode, entry.solution])
  );

  const {
    submissions,
    refresh,
    statusOverride,
    completedCtaLabel,
    showCloseCta,
  } = useCappedCodeQuiz({
    id,
    prompts: entries.map((entry) => entry.challenge),
    language,
  });

  return (
    <QuizInteractiveFlow
      id={id}
      title={title}
      illustrationKind="code_assertion"
      size="wide"
      supportsInProgress
      statusOverride={statusOverride}
      completedCtaLabel={completedCtaLabel}
      showCloseCta={showCloseCta}
      showCompletedBadge={false}
      renderQuiz={() => (
        <QuizCodeAssertion
          entries={entries}
          onValidate={onValidate}
          initialSubmissions={submissions}
          onSubmitted={refresh}
        />
      )}
    />
  );
};

export default QuizCodeAssertionFlow;
